/**
 * The "Kiff" design system — tokens and MUI themes.
 *
 * Source of truth: design/DESIGN_BRIEF.md in the monorepo plus the Claude
 * design handoff (PetTrek System.dc.html). The rules that everything else
 * hangs off:
 *
 *   - Cobalt #1D46D8 leads, coral #E8481C accents; ink #191512 does the work
 *     shadows used to fake. Pages sit on warm paper #FFF8F0, never pure white.
 *   - Elevation IS keylines: `shadows` is all 'none'. Depth comes from a
 *     2.5px ink border, and — on raised elements only — a hard offset shadow
 *     (`4px 4px 0`) that collapses on :active with a translate. That press is
 *     the one signature interaction; keep transitions <=100ms.
 *   - Type: Archivo 900 uppercase for headings/buttons, Schibsted Grotesk for
 *     body, IBM Plex Mono (tabular) for money and ids. "R137.50", never "$".
 *   - Roles are theme-level: owner = warm paper + cobalt; admin = cool grey +
 *     violet, deliberately unglamorous. Mint and violet are role-reserved —
 *     never use them on any other surface.
 */
import { createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';

// ── Core tokens ─────────────────────────────────────────────────────────────
export const INK = '#191512';
export const PAPER = '#FFF8F0';
export const COBALT = '#1D46D8';
export const COBALT_DARK = '#15308F';
export const COBALT_TINT = '#DCE6FF';
export const CORAL = '#E8481C';
export const CORAL_DARK = '#B02A0E';
export const CORAL_TINT = '#FFDCD2';
export const TEXT_SECONDARY = '#4A413A';
export const MUTED = '#75695E';
export const DIVIDER = '#E5D9CC';
export const SHADOW_COBALT = '#BFD0FF'; // offset-shadow tint for cobalt cards
export const SHADOW_WARM = '#FFC79E';   // offset-shadow tint for warm cards

// Role-reserved. Handler surfaces only / admin surfaces only.
export const MINT = '#8CF5B6';
export const VIOLET = '#7A3BD8';

// Dark "dispatcher" shell surfaces (handler dashboard only): raised tiles sit
// slightly off the ink ground, keylines are drawn a shade lighter still, and
// secondary text warms toward paper rather than grey.
export const INK_RAISED = '#241F1B';
export const INK_LINE = '#3A332C';
export const PAPER_MUTED = '#C9BFB6';

export const FONT_DISPLAY = '"Archivo", "Inter", system-ui, sans-serif';
export const FONT_BODY = '"Schibsted Grotesk", "Inter", system-ui, sans-serif';
export const FONT_MONO = '"IBM Plex Mono", "Menlo", monospace';

/** sx for money values: mono, tabular digits so columns of amounts align. */
export const moneySx = {
  fontFamily: FONT_MONO,
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
};

/**
 * Booking-state chip colours (AA on their fills). Tinted fill + 1.5px border
 * drawn in the text colour; cancelled additionally strikes its label through.
 */
export const STATUS_CHIP_STYLES = {
  pending: { color: '#5C3B00', borderColor: '#8C5A00', backgroundColor: '#FFE9A8' },
  accepted: { color: '#15308F', borderColor: '#1D46D8', backgroundColor: '#DCE6FF' },
  in_progress: { color: '#0A5C33', borderColor: '#0A5C33', backgroundColor: '#C9F0D8' },
  completed: { color: '#191512', borderColor: '#191512', backgroundColor: '#EFE7DC' },
  cancelled: {
    color: '#75695E', borderColor: '#75695E', backgroundColor: '#EFE9E1',
    textDecoration: 'line-through',
  },
  no_show: { color: '#8A2008', borderColor: '#B02A0E', backgroundColor: '#FFDCD2' },
};

/** The hard offset shadow. `tint` defaults to ink (buttons); cards use tints. */
export const offsetShadow = (tint = INK, px = 4) => `${px}px ${px}px 0 ${tint}`;

/** The signature press: shadow collapses, element moves into its own shadow. */
export const pressable = (tint = INK, px = 4) => ({
  boxShadow: offsetShadow(tint, px),
  transition: 'box-shadow 80ms ease, transform 80ms ease',
  '&:active': { boxShadow: 'none', transform: `translate(${px}px, ${px}px)` },
});

/**
 * Striped placeholder fill for missing photos (pets, handlers, hero art).
 * Placeholders are part of the design, not an apology — warm by default,
 * pass the cobalt pair for blue surfaces.
 */
export const stripeSx = (a = '#FFE1C9', b = '#FFD4B3') => ({
  backgroundImage: `repeating-linear-gradient(45deg, ${a} 0 10px, ${b} 10px 20px)`,
});

// ── Base theme (owner site) ─────────────────────────────────────────────────
const baseOptions = {
  palette: {
    primary: { main: COBALT, dark: COBALT_DARK, light: COBALT_TINT, contrastText: '#FFF' },
    secondary: { main: CORAL, dark: CORAL_DARK, light: CORAL_TINT, contrastText: '#FFF' },
    text: { primary: INK, secondary: TEXT_SECONDARY, disabled: MUTED },
    background: { default: PAPER, paper: '#FFFFFF' },
    divider: DIVIDER,
    success: { main: '#0A5C33', light: '#C9F0D8', contrastText: '#FFF' },
    warning: { main: '#8C5A00', light: '#FFE9A8', contrastText: '#FFF' },
    error: { main: CORAL_DARK, light: CORAL_TINT, contrastText: '#FFF' },
    info: { main: COBALT, light: COBALT_TINT, contrastText: '#FFF' },
    grey: { 50: PAPER, 100: '#EFE7DC' },
  },
  typography: {
    fontFamily: FONT_BODY,
    // Display size for hero headlines; pages step down from h1.
    h1: {
      fontFamily: FONT_DISPLAY, fontWeight: 900, textTransform: 'uppercase',
      fontSize: 28, letterSpacing: '-0.01em', lineHeight: 1.05,
    },
    h2: { fontFamily: FONT_DISPLAY, fontWeight: 900, textTransform: 'uppercase', fontSize: 20 },
    h3: { fontFamily: FONT_DISPLAY, fontWeight: 900, textTransform: 'uppercase', fontSize: 15 },
    h4: { fontFamily: FONT_DISPLAY, fontWeight: 900, textTransform: 'uppercase', fontSize: 24 },
    h5: { fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 18 },
    h6: { fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 16 },
    subtitle1: { fontWeight: 600 },
    button: { fontFamily: FONT_DISPLAY, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em' },
    body1: { fontSize: 15, lineHeight: 1.5 },
    body2: { fontSize: 13, lineHeight: 1.45 },
    caption: { fontFamily: FONT_MONO, fontSize: 11 },
  },
  shape: { borderRadius: 10 },
  // Depth is drawn with keylines and hard offsets — never blur.
  shadows: Array(25).fill('none'),
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: PAPER },
        // Focus must be unmissable, everywhere.
        '*:focus-visible': { outline: `3px solid ${COBALT}`, outlineOffset: 2 },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { minHeight: 44, borderRadius: 10, paddingInline: 18 },
        containedPrimary: {
          border: `2.5px solid ${INK}`,
          ...pressable(INK),
          '&:hover': { backgroundColor: COBALT_DARK, boxShadow: offsetShadow(INK) },
          '&.Mui-disabled': { boxShadow: 'none', border: `2.5px solid ${MUTED}` },
        },
        containedSecondary: {
          border: `2.5px solid ${INK}`,
          ...pressable(INK),
          '&:hover': { backgroundColor: CORAL_DARK, boxShadow: offsetShadow(INK) },
          '&.Mui-disabled': { boxShadow: 'none', border: `2.5px solid ${MUTED}` },
        },
        outlined: {
          border: `2px solid ${INK}`, color: INK,
          '&:hover': { border: `2px solid ${INK}`, backgroundColor: '#EFE7DC' },
        },
        text: { color: COBALT, fontWeight: 800 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          border: `2.5px solid ${INK}`, borderRadius: 14, backgroundImage: 'none',
        },
      },
    },
    // AppBar extends Paper — without this it inherits the card keyline and
    // radius, drawing a rounded box around the whole top bar. The bar draws
    // its own role-coloured borderBottom in Navbar.
    MuiAppBar: { styleOverrides: { root: { border: 'none', borderRadius: 0 } } },
    // Menus/popovers ride on Paper; a 2.5px keyline overwhelms them.
    MuiMenu: { styleOverrides: { paper: { border: `1.5px solid ${INK}`, borderRadius: 10 } } },
    MuiDialog: { styleOverrides: { paper: { border: `2.5px solid ${INK}`, borderRadius: 14 } } },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, border: '2px solid currentColor', fontWeight: 500 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 11,
          textTransform: 'uppercase', border: '1.5px solid currentColor', borderRadius: 99,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2, borderColor: INK },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2, borderColor: COBALT },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 13 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { fontFamily: FONT_DISPLAY, fontWeight: 800, textTransform: 'uppercase', minHeight: 44 },
      },
    },
    MuiLink: { styleOverrides: { root: { color: COBALT, fontWeight: 700 } } },
    MuiAvatar: { styleOverrides: { root: { border: `2px solid ${INK}` } } },
  },
};

export const kiffTheme = createTheme(baseOptions);

// ── Admin theme (cool grey + violet; deliberately unglamorous) ──────────────
export const adminTheme = createTheme(deepmerge(baseOptions, {
  palette: {
    primary: { main: VIOLET, dark: '#5B2AA6', light: '#E9DFF8', contrastText: '#FFF' },
    background: { default: '#EFF0F4', paper: '#FFFFFF' },
    text: { secondary: '#5A5670' },
    divider: '#E6E5EE',
  },
  components: {
    MuiCssBaseline: { styleOverrides: { body: { backgroundColor: '#EFF0F4' } } },
    // Quiet register everywhere: thin cool borders, no offset shadows,
    // no sticker energy. Data reads in mono; status words use the booking
    // state text colours.
    MuiPaper: {
      styleOverrides: { root: { border: '1.5px solid #D6D4E0', borderRadius: 10 } },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: { border: 'none', boxShadow: 'none', '&:active': { transform: 'none' } },
        containedSecondary: { border: 'none', boxShadow: 'none', '&:active': { transform: 'none' } },
      },
    },
  },
}));

/**
 * Handler-site theme.
 *
 * The reference design is a dark "dispatcher" shell: ink ground, mint
 * accents, light job cards that pop against it. The ground flip is applied
 * by HandlerDashboard itself (the shared pages — auth, profile, payments —
 * keep the paper ground), so the palette here stays ink-on-light for card
 * interiors. What changes at theme level is the accent: primary is the
 * role-reserved mint, drawn with ink text because mint is a light fill.
 * Base body is bumped for readability outdoors.
 */
export const handlerTheme = createTheme(deepmerge(baseOptions, {
  palette: {
    primary: { main: MINT, dark: '#4FCE87', light: '#D9FBE8', contrastText: INK },
  },
  typography: {
    body1: { fontSize: 16, lineHeight: 1.5 },
    h3: { fontSize: 17 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        // The base theme hardcodes the cobalt hover; mint needs its own, and
        // the label must stay ink on a light fill.
        containedPrimary: { color: INK, '&:hover': { backgroundColor: '#4FCE87' } },
      },
    },
  },
}));
